import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import moment from "moment";
import styled from "styled-components";
import { decideFileType } from "app/popleads/modules/activities/utils";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "_metronic/_helpers";
import FileCard from "app/popleads/components/ui-kit/FileCard";
import CircularProgress from "app/popleads/components/ui-kit/CircularProgress";
import { useExtensionContext } from "../ExtensionContext";
import { post } from "app/popleads/networking/RequestService";
import { colors } from "app/popleads/components/ui-kit/colors";
import { toast } from "react-toastify";
import Modal from "app/popleads/components/ui-kit/Modal";
import Spinner from "app/popleads/components/ui-kit/Spinner";
import { ProfileLoadingOverlay } from "../ExtensionHome";
import { useSelector } from "react-redux";
import MaterialIcon from "app/popleads/components/ui-kit/MaterialIcon";

const Container = styled.div`
    padding: 20px;
    width: 100%;
    min-height: 300px;
    position: relative;
    overflow-y: scroll;
    ${(props) =>
        props.isDragActive &&
        `
        &:after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 133, 190, 0.05);
            z-index: 1;
            pointer-events: none;
        }
    `}
`;

const DotContainer = styled.div`
    border: 2px dashed ${colors.neutral[300]};
    border-radius: 8px;
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 16px;
    text-align: center;
    cursor: pointer;
    margin-bottom: 20px;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: row;
    align-items: center;
    background: #f6f8f9;
    position: relative;
    z-index: 2;

    &:hover {
        border-color: ${colors.l3rnTech[900]};
    }

    .upload-icon {
        font-size: 24px;
        color: #1890ff;
        margin-bottom: 8px;
    }

    .upload-text {
        color: #666;
        margin-bottom: 4px;
        font-size: 14px;
        letter-spacing: -0.17px;
    }

    .file-types {
        color: #8c8c8c;
        font-size: 12px;
    }
    .file-name {
        -webkit-line-clamp: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 70%;
    }
`;

const TextsInDotContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    .underline-text {
        color: ${colors.l3rnTech[900]};
        text-decoration: underline;
    }
`;

const IconInDotContainer = styled.div`
    color: #5f6368;
    margin-right: 16px;
`;

const FileList = styled.div``;
const StyledFileName = styled.div`
    -webkit-line-clamp: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 340px;
`;
const ColoredDeleteButton = styled.span`
    color: ${colors.danger[900]};
`;

export default function ExtensionFilesAndAttachments({ onBack, showBackButton }) {
    const [files, setFiles] = useState([]);
    const [fileToDelete, setFileToDelete] = useState(null);
    const {
        profile,
        selectedModal,
        modals,
        setState,
        profileNotesRefetch,
        refetchAttachments,
        refetchDeals,
    } = useExtensionContext();
    const [deleteFileLoading, setDeleteFileLoading] = useState(false);
    const { user: loggedInUser } = useSelector((state) => state.auth);

    useEffect(() => {
        if (profile?.attachments) {
            const sortFiles = profile.attachments.sort((a, b) => {
                const dateA = new Date(a.createdTime);
                const dateB = new Date(b.createdTime);
                return dateB - dateA; // Sort in descending order
            });
            const loadedFiles = sortFiles.map((attachment) => ({
                file: null,
                id: attachment?.id,
                name: attachment?.filename,
                timestamp: moment(attachment?.createdTime).format("MMM DD, YYYY hh:mm"),
                uploading: false,
                progress: 100,
                downloadLink: attachment?.downloadLink,
                userName: attachment?.userName,
            }));
            setFiles(loadedFiles);
        }
    }, [profile]);

    const onDrop = async (acceptedFiles) => {
        const newFiles = acceptedFiles.map((file) => ({
            file,
            id: `${file.name}-${moment().valueOf()}`,
            name: file.name,
            timestamp: moment().format("MMM DD, YYYY"),
            uploading: true,
            progress: 0,
            userName: loggedInUser?.name || "Unknown User",
        }));

        setFiles((prev) => [...newFiles, ...prev]);

        for (const file of newFiles) {
            await handleUpload(file.file, (progress) => {
                updateFileProgress(file.id, progress, progress < 100);
            });
        }
        if (newFiles.length > 0) {
            //refetchProfile();
            refetchAttachments();
        }
    };

    const updateFileProgress = (fileID, progress, uploading = true) => {
        setFiles((prevFiles) => {
            return prevFiles.map((file) => (file.id === fileID ? { ...file, progress, uploading } : file));
        });
    };

    const handleUpload = async (file, updateProgress) => {
        const uploadAttachmentForExtension = {
            fn: "uploadAttachment",
            profilId: profile?.id,
            fileType: "Regular",
            file,
        };
        try {
            const response = await post(uploadAttachmentForExtension);
            let simulatedProgress = 0;
            const interval = setInterval(() => {
                simulatedProgress += 30;
                updateProgress(simulatedProgress);
                if (simulatedProgress >= 100) {
                    clearInterval(interval);
                    updateProgress(100);
                }
            }, 100);

            if (response.status === 200) {
                toast.success("File uploaded successfully.");
            } else {
                toast.error("Upload failed");
            }
        } catch (error) {
            toast.error("File upload failed. Please try again.");
            updateProgress(0);
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "application/pdf": [".pdf"],
            "application/msword": [".doc"],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
            "application/vnd.ms-excel": [".xls"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
            "text/plain": [".txt"],
            "video/mp4": [".mp4"],
            "text/csv": [".csv"],
            "image/svg+xml": [".svg"],
            "application/zip": [".zip"],
            "application/x-rar-compressed": [".rar"],
        },
        multiple: true,
        onDropRejected: () => {
            toast.error("Please upload only DOC, XLS, TXT, PDF, CSV, ZIP, RAR, MP4, JPEG, PNG, or SVG files");
        },
        noClick: true,
    });

    const handleDeleteAttachment = async (fileId) => {
        setState({ selectedModal: null });
        try {
            const deleteObj = {
                fn: "deleteAttachment",
                attachmentId: fileId,
            };
            setDeleteFileLoading(true);
            const response = await post(deleteObj);

            if (response.status === 200) {
                //refetchProfile();
                refetchAttachments();
                profileNotesRefetch();
                refetchDeals();
                toast.success("Attachment deleted.");
            } else {
                toast.error("Failed to delete file");
                setState({ modalOpen: false });
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.text || error?.message || "Failed to delete file";
            toast.error(errorMessage);
            setState({ modalOpen: false });
            setDeleteFileLoading(false);
        } finally {
            setDeleteFileLoading(false);
        }
    };

    return (
        <>
            {deleteFileLoading && (
                <ProfileLoadingOverlay>
                    <Spinner size={32} thickness={4} variant="l3rnTech" />
                </ProfileLoadingOverlay>
            )}
            <Container {...getRootProps()} isDragActive={isDragActive}>
                <input {...getInputProps()} />
                <DotContainer onClick={() => document.querySelector('input[type="file"]').click()}>
                    <IconInDotContainer>
                        <MaterialIcon icon="cloud_upload" />
                    </IconInDotContainer>
                    <TextsInDotContainer>
                        <div className="upload-text">
                            {isDragActive ? (
                                "Drop the files here"
                            ) : (
                                <>
                                    Drag and Drop files here or <span className="underline-text">browse computer</span>
                                </>
                            )}
                        </div>
                        <div className="file-types">File supported: DOC, XLS, TXT, PDF, JPEG, PNG</div>
                    </TextsInDotContainer>
                </DotContainer>

                <FileList>
                    {files.map((file) => (
                        <FileCard
                            key={file.id}
                            isUploading={file.uploading}
                            leftIcon={
                                <CircularProgress
                                    progress={file.uploading ? file.progress : 100}
                                    size={32}
                                    color={colors.l3rnTech[900]}
                                    isLoading={file.uploading}
                                >
                                    <a
                                        target="_blank"
                                        href={file.downloadLink}
                                        style={{
                                            fontSize: "20px",
                                            color: colors.l3rnTech[900],
                                        }}
                                    >
                                        <MaterialIcon
                                            icon={file.uploading ? "arrow_upward" : "arrow_downward"}
                                            style={{ fontSize: "20px", color: colors.l3rnTech[900] }}
                                        />
                                    </a>
                                </CircularProgress>
                            }
                            content={
                                <div className="d-flex flex-row align-items-center">
                                    {file.downloadLink ? (
                                        <a
                                            href={file.downloadLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="d-flex flex-row align-items-center text-dark text-decoration-none"
                                        >
                                            <div className="mr-5">
                                                <SVG
                                                    src={toAbsoluteUrl(decideFileType(file.name))}
                                                    style={{ width: 24, height: 24 }}
                                                />
                                            </div>
                                            <div className="d-flex flex-column align-items-start">
                                                <StyledFileName>{file.name}</StyledFileName>
                                                <div className="file-meta">{`${
                                                    file?.userName ? file?.userName : ""
                                                } | ${file.timestamp}`}</div>
                                            </div>
                                        </a>
                                    ) : (
                                        <>
                                            <div className="mr-5">
                                                <SVG
                                                    src={toAbsoluteUrl(decideFileType(file.name))}
                                                    style={{ width: 24, height: 24 }}
                                                />
                                            </div>
                                            <div className="d-flex flex-column align-items-start">
                                                <StyledFileName>{file.name}</StyledFileName>
                                                <div className="file-meta">{`${
                                                    file?.userName ? file?.userName : ""
                                                } | ${file.timestamp}`}</div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            }
                            rightIcon={
                                !file.uploading && (
                                    <ColoredDeleteButton
                                        onClick={() => {
                                            setState({ selectedModal: modals.DELETE_ATTACHMENT });
                                            setFileToDelete(file.id);
                                        }}
                                    >
                                        <MaterialIcon icon="delete" style={{ fontSize: "20px" }} />
                                    </ColoredDeleteButton>
                                )
                            }
                        />
                    ))}
                </FileList>

                <Modal
                    isOpen={selectedModal === modals.DELETE_ATTACHMENT}
                    onClose={() => setState({ selectedModal: null })}
                    title="Are you sure you want to delete this file?"
                    variant="danger"
                    icon="delete"
                    successButtonText="Yes, Delete"
                    cancelButtonText="Not Now"
                    onSuccess={() => handleDeleteAttachment(fileToDelete)}
                >
                    <div>This action cannot be undone.</div>
                </Modal>
            </Container>
        </>
    );
}
